/**
 * Atlas form ↔ ``factory_presets.params`` translation. The backend strips
 * ``channel`` / ``count`` server-side, but we omit them here too so the
 * persisted JSON is clean. ``force`` and ``projection`` are also excluded
 * — neither is form-controlled (``force`` is a submit-time flag,
 * ``projection`` is hardcoded in AtlasForm), so persisting them in a
 * preset would just freeze a stale value.
 */

import type { components } from '@/../types/api'

export type AtlasPayload = components['schemas']['AtlasStartIn']
// ``llm_provider`` fica de fora do preset: o toggle Local/Nuvem é decisão
// por-run (o backend defaulta pra ``auto``), não algo a congelar no preset.
type AtlasPresetPayload = Omit<AtlasPayload, 'channel' | 'count' | 'force' | 'projection' | 'llm_provider'>

export type RegionKey =
  | 'world'
  | 'africa'
  | 'asia'
  | 'europe'
  | 'north_america'
  | 'oceania'
  | 'south_america'

export type DurationKey = 'short' | 'medium' | 'long'
export type ToneKey = 'formal' | 'curious' | 'sarcastic'

export interface FormValues {
  theme: string
  count: number
  language: 'pt-br' | 'en' | 'both'
  region_scope: RegionKey
  duration_preset: DurationKey
  render_scale: 1 | 2 | 3
  custom_prompt?: string
  tone?: ToneKey
  tts_voice_pt?: string
  tts_voice_en?: string
}

const FORM_DEFAULTS: FormValues = {
  theme: 'atlas-world_oddities',
  count: 1,
  language: 'pt-br',
  region_scope: 'world',
  duration_preset: 'medium',
  render_scale: 2,
  custom_prompt: '',
}

export function formToPayload(values: FormValues): AtlasPresetPayload {
  const payload: AtlasPresetPayload = {
    theme: values.theme,
    language: values.language,
    region_scope: values.region_scope,
    duration_preset: values.duration_preset,
    render_scale: values.render_scale,
    custom_prompt: values.theme === 'custom' && values.custom_prompt ? values.custom_prompt : '',
  }
  if (values.tone) payload.tone = values.tone
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

export function payloadToForm(params: Record<string, unknown>): FormValues {
  const values: FormValues = {
    ...FORM_DEFAULTS,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    count: (params.count as number | undefined) ?? FORM_DEFAULTS.count,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    region_scope: (params.region_scope as RegionKey | undefined) ?? FORM_DEFAULTS.region_scope,
    duration_preset:
      (params.duration_preset as DurationKey | undefined) ?? FORM_DEFAULTS.duration_preset,
    render_scale:
      (params.render_scale as FormValues['render_scale'] | undefined) ?? FORM_DEFAULTS.render_scale,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
  }
  if (params.tone) values.tone = params.tone as ToneKey
  if (params.tts_voice_pt) values.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) values.tts_voice_en = params.tts_voice_en as string
  return values
}
